import React, { useMemo } from "react";
import { Platform, Text as RNText, View } from "react-native";
import { Banner, Icon, ProgressBar, Text, useTheme } from "react-native-paper";
import {
  episodePhrases,
  extractPrefixed,
  processIdentifier,
  processScript,
  ScriptScene,
} from "../common/file-import";
import {
  CollectionReference,
  DocumentReference,
  DocumentSnapshot,
} from "@atgof-firebase/firebase";
import { LanguageContext } from "../common/language";
import { ImporterComponentProps } from "../screens/Importer";
import { Revealer } from "./Revealer";
import ScriptParagraph from "../common/ScriptParagraph";
import SceneInfo, {
  PreexistingSubjects,
  usePreexistingSubjects,
} from "./SceneInfo";
import { useSnapshot } from "../data/useSnapshot";
import { ProjectContext } from "../data/projectContext";
import { scenesCollection, sceneLabel, toScene } from "../common/scene";
import ReferentSelector from "./ReferentSelector";
import { getEpisodePath } from "../common/episode";
import { useDocument } from "../data/useDocument";
import { PhraseFunction } from "../common/phrases";
import { episodeLabel, UNKNOWN_LABEL } from "../common/util";
import { addEpisode } from "../data/episode";
import { UserContext } from "../data/userContext";

function parseEpisodeIdFromFilename(importEntry: DocumentSnapshot | undefined) {
  const metadata: { name: string } | undefined = importEntry?.get(
    "originalFileMetadata",
  );
  if (!metadata) return;
  const filename = metadata.name;
  for (const part of filename.split(/_|(\s\-\s)/)) {
    const prefixed = extractPrefixed(episodePhrases, part, true);
    const m = prefixed?.match(/^(\d+)/);
    if (m) return processIdentifier(m[1]);
  }
  const episode = filename.match(/(?<episode>([A-Z]?\d*)|(\d+))[\.\/]\d+/)
    ?.groups?.episode;
  if (episode) return processIdentifier(episode);
}

async function updateEpisode(
  episodeRef: DocumentReference,
  cast: string[] | undefined,
  location: string | undefined,
) {
  const episode = (await episodeRef.get()).data();
  const episodeCast = new Set([...(episode?.cast ?? []), ...(cast ?? [])]);
  const episodeSets = new Set([
    ...(episode?.sets ?? []),
    ...(location ? [location] : []),
  ]);
  const episodeData = {
    sceneCount: (episode?.sceneCount ?? 0) + 1,
    cast: [...episodeCast].sort(),
    sets: [...episodeSets].sort(),
  };
  if (!episode) {
    episodeRef.set({
      deleted: false,
      displayName: episodeRef.id,
      ...episodeData,
    });
  } else episodeRef.update(episodeData);
}

async function updateSubjects(
  coll: CollectionReference | undefined,
  values: string[],
) {
  if (!coll) return;
  const toAdd = new Set(values);
  for (const doc of (await coll.get()).docs) {
    toAdd.delete(doc.get("displayName")); // TODO Take into account doc.get('deleted')
  }
  for (const value of toAdd) {
    coll.add({
      deleted: false,
      displayName: value,
    });
  }
}

function PromptToAddIfNeeded({
  kind,
  entityDescription,
  docRef,
  add,
  children,
}: {
  kind: "episode" | "scene";
  entityDescription: string | undefined;
  docRef: DocumentReference | undefined;
  add: (ref: DocumentReference) => void;
  children?: React.ReactNode;
}) {
  const { ph } = React.useContext(LanguageContext);
  const doc = useDocument(docRef);
  if (!docRef || doc?.exists) return null;
  return (
    <Banner
      visible
      icon="alert-rhombus"
      actions={[
        {
          label: ph("add") + " " + ph(kind),
          onPress: () => add(docRef),
        },
      ]}
      style={
        {
          /*maxWidth: 600 TODO but this truncates long cast lists */
        }
      }
    >
      <View style={{ gap: 4 }}>
        <Text>
          {
            (ph("does-not-exist-prompt") as PhraseFunction)({
              entityDescription: entityDescription ?? UNKNOWN_LABEL,
            }) as string
          }
        </Text>
        {children}
      </View>
    </Banner>
  );
}

function SceneSection({
  actualScene,
  baseScene,
  baseSceneRef,
  episodeRef,
  preexistingSubjects,
}: {
  actualScene: DocumentSnapshot | undefined;
  baseScene: ScriptScene;
  baseSceneRef: DocumentReference;
  episodeRef: DocumentReference | undefined;
  preexistingSubjects: PreexistingSubjects;
}) {
  const { project } = React.useContext(ProjectContext);
  const [sceneData, setSceneData] = React.useState({
    ...baseScene,
    episodeId: episodeRef?.id,
  });
  async function updateScene(data: Partial<ScriptScene>) {
    setSceneData((existingData) => ({ ...existingData, ...data }));
  }
  React.useEffect(() => {
    updateScene({ episodeId: episodeRef?.id });
  }, [episodeRef]);
  async function addScene(ref: DocumentReference) {
    if (ref.path !== baseSceneRef?.path) return; // TODO Support adding of sub-scenes
    const {
      id,
      episodeId,
      isInterior,
      isExterior,
      location,
      scriptDay,
      scriptDate,
      cast,
      timeOfDay,
      synopsis,
    } = sceneData;
    if (!(baseSceneRef && episodeRef && id && episodeId)) return;
    baseSceneRef.set({
      deleted: false,
      id,
      episodeId,
      baseSceneId: id, // TODO
      isInterior,
      isExterior,
      ...(timeOfDay ? { timeOfDay } : {}),
      sets: [location ?? ""],
      cast: cast ?? [],
      ...(synopsis ? { synopsis } : {}),
      ...(scriptDay ? { scriptDay } : {}),
      ...(scriptDate ? { scriptDate } : {}),
    });
    updateEpisode(episodeRef, cast, location);
    if (cast) updateSubjects(project?.collection("characters"), cast);
    if (location) updateSubjects(project?.collection("sets"), [location]);
  }
  return (
    <View>
      <PromptToAddIfNeeded
        kind="scene"
        entityDescription={sceneLabel(
          actualScene?.ref.path ?? baseSceneRef.path,
        )}
        docRef={actualScene?.ref ?? baseSceneRef}
        add={addScene}
      >
        <SceneInfo
          scene={sceneData}
          updateScene={updateScene}
          preexistingSubjects={preexistingSubjects}
        />
      </PromptToAddIfNeeded>
      {actualScene?.exists ? <SceneInfo scene={toScene(actualScene)} /> : null}
      <div style={{ userSelect: "text" }}>
        {baseScene.content.map((paragraph, index) => (
          <ScriptParagraph
            key={index}
            content={paragraph}
            textAnnotations={baseScene.textAnnotations.filter(
              (entry) => entry.paragraph == index,
            )}
            web={Platform.OS === "web"}
            Text={RNText}
            View={View}
          />
        ))}
      </div>
    </View>
  );
}

function ScriptSceneImporter({
  scene,
  episodeRef,
  preexistingSubjects,
}: {
  scene: ScriptScene;
  episodeRef: DocumentReference | undefined;
  preexistingSubjects: PreexistingSubjects;
}) {
  const { colors } = useTheme();
  const { ph } = React.useContext(LanguageContext);
  // TODO Warn if season is undefined
  const sceneRef = scene.id
    ? scenesCollection(episodeRef)?.doc(scene.id)
    : undefined;
  const actualScene_ = useDocument(sceneRef);
  const actualScenes_ = useSnapshot<DocumentSnapshot[] | undefined>(
    sceneRef
      ? (onNext) =>
          sceneRef.parent
            .where("baseSceneId", "==", sceneRef.id)
            .onSnapshot(({ docs }) => onNext(docs))
      : undefined,
    [sceneRef?.path],
  );
  const actualScenes = useMemo<DocumentSnapshot[] | undefined>(
    () =>
      actualScene_ && actualScenes_
        ? actualScene_.exists &&
          !actualScenes_.some((doc) => doc.ref.path === actualScene_.ref.path)
          ? [actualScene_, ...actualScenes_]
          : actualScenes_
        : sceneRef
          ? []
          : undefined,
    [actualScene_, actualScenes_],
  );
  const scenePath = actualScenes ? sceneRef?.path : undefined;
  if (!actualScenes) return <ProgressBar indeterminate />;
  return (
    <Revealer
      title={
        <View style={{ gap: 4, flexDirection: "row" }}>
          {!(episodeRef && actualScenes?.length) ? (
            <Icon size={24} source="alert" color={colors.error} />
          ) : null}
          <Text variant="titleMedium">{ph("scene") + " " + scene.id}</Text>
        </View>
      }
    >
      <View>
        {sceneRef && actualScenes?.length ? (
          actualScenes.map((actualScene) => (
            <SceneSection
              key={actualScene.ref.path}
              actualScene={actualScene}
              baseScene={scene}
              baseSceneRef={sceneRef}
              episodeRef={episodeRef}
              preexistingSubjects={preexistingSubjects}
            />
          ))
        ) : sceneRef && actualScenes ? (
          <SceneSection
            actualScene={undefined}
            baseScene={scene}
            baseSceneRef={sceneRef}
            episodeRef={episodeRef}
            preexistingSubjects={preexistingSubjects}
          />
        ) : (
          <ProgressBar indeterminate />
        )}
      </View>
    </Revealer>
  );
}

export default function ScriptImporter({
  content,
  importButton,
  importEntry,
}: ImporterComponentProps) {
  const { ph } = React.useContext(LanguageContext);
  const { user } = React.useContext(UserContext);
  const script = React.useMemo(() => processScript(content), [content]);
  const [selectedEpisode, setSelectedEpisode] =
    React.useState<DocumentReference>();
  const episodePath = getEpisodePath(selectedEpisode);
  const defaultEpisodeId =
    script.episodeId ?? parseEpisodeIdFromFilename(importEntry);
  React.useEffect(() => {
    document.addEventListener("selectionchange", () =>
      console.log("SEL", document.getSelection()),
    );
  }, []);
  const preexistingSubjects = usePreexistingSubjects();
  return (
    <View style={{ gap: 8, marginTop: 16, marginBottom: 16 }}>
      <ReferentSelector
        kind="episode"
        defaultReferentId={defaultEpisodeId}
        referent={selectedEpisode}
        onReferentChange={setSelectedEpisode}
        allowOther
      />
      <PromptToAddIfNeeded
        kind="episode"
        entityDescription={episodeLabel(ph, selectedEpisode?.path)}
        docRef={selectedEpisode}
        add={(ref) => addEpisode(user.ref, ref)}
      />
      {script.scenes.map((scene) => (
        <ScriptSceneImporter
          key={scene.id}
          scene={scene}
          episodeRef={selectedEpisode}
          preexistingSubjects={preexistingSubjects}
        />
      ))}
      {script.scenes && importButton && (
        <View style={{ gap: 8, alignSelf: "center" }}>
          {selectedEpisode &&
            importButton(
              episodePath
                ? {
                    seasonId: episodePath.season,
                    ...(episodePath.episode
                      ? { episodeId: episodePath.episode }
                      : {}),
                  }
                : {},
            )}
        </View>
      )}
    </View>
  );
}
